import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

// material ui
import { DataGrid } from '@mui/x-data-grid/DataGrid';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Button, TextField, LinearProgress } from '@mui/material';

import usePedsReviewAPI from './hooks/usePedsReviewAPI';
import styles from '../Datagrid/Datagrid.styles';

import DatagridFooter from '../Datagrid/DatagridFooter';
import DatagridNoRowsOverlay from '../Datagrid/DatagridNoRowsOverlay';
import Chip from '../Snapshot/Chip';

import themev5 from '../../themev5';

import tableStyles from '../Snapshot/styles/table-root';
import Select from '../Snapshot/Select';
import { CENTER_OPTIONS } from './constants';
import { getPedsReviewColumns } from './constants';

const PedsReview = () => {
  const history = useHistory();
  const ref = useRef<HTMLInputElement>(null);

  const searchParams = new URLSearchParams(history.location.search);

  const {
    reviews,
    loading,
    totalCount,
    center,
    searchText,
    page,
    pageSize,
    setFilters,
    setCenterOnly,
    setSearchOnly,
    clearFilters,
    changePage,
    changePageSize
  } = usePedsReviewAPI();

  const columns = getPedsReviewColumns((row: any) => {
    history.push({
      pathname: `/peds-review/${row.id}`,
      state: { row }
    } as any);
  });

  useEffect(() => {
    // Initialize from URL params
    const urlSearchText = searchParams.get('searchText') || '';
    const urlCenter = searchParams.get('center') || 'all';
    const urlPageNo = parseInt(searchParams.get('pageNo') || '1', 10);
    const urlPageSize = parseInt(searchParams.get('pageSize') || '20', 10);

    changePage(urlPageNo);
    changePageSize(urlPageSize);
    setFilters(urlCenter, urlSearchText);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Update URL with current state
    const params = new URLSearchParams();
    params.append('pageNo', page.toString());
    params.append('pageSize', pageSize.toString());
    if (center && center !== 'all') {
      params.append('center', center);
    }
    if (searchText) {
      params.append('searchText', searchText);
    }

    const queryString = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }, [page, pageSize, center, searchText]);

  const handleSearchClick = () => {
    const newSearchText = (ref.current?.value as string) || '';
    setFilters(center, newSearchText);
  };

  const handleCenterChange = (event: any) => {
    const currentSearchText = (ref.current?.value as string) || searchText;
    setFilters(event.target.value, currentSearchText);
  };

  const handleClearClick = () => {
    clearFilters();
    if (ref.current) {
      ref.current.value = '';
    }
  };

  return (
    <ThemeProvider theme={themev5}>
      <CssBaseline />
      <Box display='flex' ml={2} mt={10} alignItems='center'>
        <Select
          label='Center Type'
          value={center || 'all'}
          onChange={handleCenterChange}
          options={CENTER_OPTIONS}
        />
        <TextField
          variant='outlined'
          type='search'
          id='search_application'
          label='Search Application Number'
          defaultValue={searchText}
          inputRef={ref}
          onKeyPress={(event: any) => event.key === 'Enter' && handleSearchClick()}
          InputProps={{
            style: {
              minWidth: '250px'
            }
          }}
        />
        <Button sx={{ ml: 1 }} variant='contained' color='primary' onClick={handleSearchClick}>
          Search
        </Button>
        <Button sx={{ ml: 1 }} variant='contained' color='primary' onClick={handleClearClick}>
          Clear
        </Button>
      </Box>
      <Box display='flex' ml={2} gap={4}>
        {(center || 'all') !== 'all' && (
          <Chip
            title={center || 'all'}
            value={center || 'all'}
            onClose={() => setCenterOnly('all')}
          />
        )}
        {searchText && (
          <Chip
            title={searchText}
            value={searchText}
            onClose={() => {
              setSearchOnly('');
              if (ref.current) {
                ref.current.value = '';
              }
            }}
          />
        )}
      </Box>
      <Box
        height='calc(100vh - 180px)'
        p={2}
        display='flex'
        alignItems='center'
        justifyContent='center'
        sx={tableStyles.root as any}>
        <DataGrid
          sx={styles.datagrid}
          rowSpacingType='margin'
          sortingOrder={['desc', 'asc']}
          checkboxSelection={false}
          density='standard'
          rows={reviews}
          columns={columns as any}
          pageSize={pageSize}
          rowCount={totalCount}
          getRowId={row => row.id.toString()}
          onPageSizeChange={changePageSize}
          onPageChange={(newPage: number) => changePage(newPage + 1)}
          rowsPerPageOptions={[20, 40, 60, 80]}
          pagination
          loading={loading}
          getRowClassName={() => `table-rows`}
          page={page - 1}
          paginationMode='server'
          components={{
            LoadingOverlay: LinearProgress,
            Footer: DatagridFooter,
            NoRowsOverlay: DatagridNoRowsOverlay
          }}
        />
      </Box>
    </ThemeProvider>
  );
};

export default PedsReview;
